import React, { useEffect, useState, useRef, useCallback } from "react";
import { db, auth, storage } from "../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  getDocs,
  limit,
  deleteField
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import styles from "../styles/Groups.module.css";
// Basic icons replacement since we don't have react-icons installed or imported in the snippet
// using simple text/emoji or generic spans if icons are missing, but user had emojis before.
// We will stick to emojis/text as in the original to be safe, or use the existing react-icons if available.
// The original code used emojis. I will stick to emojis to ensure no broken icon imports.

// WebSocket setup for real-time messaging
let socket = null;

export default function Groups() {
  const [chats, setChats] = useState([]); // All chats (groups + DMs)
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [searchUsers, setSearchUsers] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showGroupCreate, setShowGroupCreate] = useState(false);
  const [groupForm, setGroupForm] = useState({
    name: "",
    participants: []
  });
  const [typingStatus, setTypingStatus] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // For searching chats
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize WebSocket connection
  const initWebSocket = (userId) => {
    if (socket) {
      socket.close();
    }
    console.log("WebSocket initialized for user:", userId);
  };

  // Get current user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
      if (authUser) {
        initWebSocket(authUser.uid);
        loadUserChats(authUser.uid);
      }
      setLoading(false);
    });

    return () => {
      if (socket) {
        socket.close();
      }
      unsubscribe();
    };
  }, []);

  // Load user's chats (groups + DMs)
  const loadUserChats = async (userId) => {
    try {
      // Load groups where user is a member
      const groupsQuery = query(
        collection(db, "groups"),
        where("members", "array-contains", userId),
        orderBy("lastMessageAt", "desc")
      );

      const groupsUnsubscribe = onSnapshot(groupsQuery, (snapshot) => {
        const groupsData = snapshot.docs.map(doc => ({
          id: doc.id,
          type: "group",
          ...doc.data()
        }));

        // Load DMs where user is a participant
        loadDMs(userId, groupsData);
      });
    } catch (err) {
      console.error("Error loading chats:", err);
    }
  };

  // Handle navigation state to open a specific group
  useEffect(() => {
    if (location.state?.groupId && chats.length > 0) {
      const targetGroup = chats.find(c => c.id === location.state.groupId);
      if (targetGroup) {
        setSelectedChat(targetGroup);
        // Clear state to prevent reopening on generic re-renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, chats]);

  // Load direct messages
  const loadDMs = async (userId, groupsList) => {
    try {
      const dmsQuery = query(
        collection(db, "direct_messages"),
        where("participants", "array-contains", userId),
        orderBy("lastMessageAt", "desc")
      );

      const dmsUnsubscribe = onSnapshot(dmsQuery, (snapshot) => {
        const dmsData = snapshot.docs.map(doc => ({
          id: doc.id,
          type: "dm",
          ...doc.data()
        }));

        // Load participants info for DMs
        const loadUserInfo = async () => {
          const updatedDms = await Promise.all(
            dmsData.map(async (dm) => {
              const otherUserId = dm.participants.find(p => p !== userId);
              if (otherUserId) {
                try {
                  const userDoc = await getDoc(doc(db, "users", otherUserId));
                  if (userDoc.exists()) {
                    return { ...dm, otherUser: userDoc.data() };
                  }
                } catch (err) {
                  console.error("Error loading user info:", err);
                }
              }
              return dm;
            })
          );

          // Combine and set chats
          setChats([...groupsList, ...updatedDms]);
        };

        loadUserInfo();
      });
    } catch (err) {
      console.error("Error loading DMs:", err);
    }
  };



  // Filter chats based on search term
  const filteredChats = chats.filter(chat => {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();
    if (chat.type === "group") {
      return chat.name.toLowerCase().includes(term);
    } else {
      const otherUser = chat.otherUser;
      if (!otherUser) return false;
      return (
        (otherUser.name || "").toLowerCase().includes(term) ||
        (otherUser.email || "").toLowerCase().includes(term) ||
        (otherUser.username || "").toLowerCase().includes(term)
      );
    }
  });

  // Search users for new chat
  useEffect(() => {
    if (!searchUsers.trim() || !user) {
      setSearchResults([]);
      return;
    }

    const searchForUsers = async () => {
      try {
        const usersQuery = query(
          collection(db, "users"),
          where("email", ">=", searchUsers),
          where("email", "<=", searchUsers + "\uf8ff"),
          limit(10)
        );

        const snapshot = await getDocs(usersQuery);
        const users = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(u => u.id !== user.uid); // Exclude current user

        setSearchResults(users);
      } catch (err) {
        console.error("Search error:", err);
      }
    };

    const timeoutId = setTimeout(searchForUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchUsers, user]);

  // Handle typing indicator
  const handleTyping = () => {
    if (!selectedChat || !user) return;
    setIsTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2000);
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;
    if (!selectedChat || !user) return;

    try {
      const messageData = {
        senderId: user.uid,
        senderName: user.displayName || user.email.split('@')[0],
        senderEmail: user.email,
        text: newMessage.trim(),
        timestamp: serverTimestamp(),
        readBy: [user.uid],
        type: selectedFile ? "image" : "text",
        status: "sent"
      };

      if (selectedFile) {
        const fileUrl = await uploadFile(selectedFile);
        messageData.fileUrl = fileUrl;
        messageData.fileName = selectedFile.name;
        messageData.fileType = selectedFile.type;
      }

      let chatRef, messagesCollection;

      if (selectedChat.type === "group") {
        chatRef = doc(db, "groups", selectedChat.id);
        messagesCollection = `group_messages/${selectedChat.id}/messages`;

        const messageRef = await addDoc(collection(db, messagesCollection), messageData);

        await updateDoc(chatRef, {
          lastMessage: newMessage.substring(0, 100) + (newMessage.length > 100 ? "..." : ""),
          lastMessageAt: serverTimestamp(),
          lastMessageSender: user.uid,
          lastMessageId: messageRef.id
        });

      } else {
        chatRef = doc(db, "direct_messages", selectedChat.id);
        messagesCollection = `direct_messages/${selectedChat.id}/messages`;

        const messageRef = await addDoc(collection(db, messagesCollection), messageData);

        await updateDoc(chatRef, {
          lastMessage: newMessage.substring(0, 100) + (newMessage.length > 100 ? "..." : ""),
          lastMessageAt: serverTimestamp(),
          lastMessageSender: user.uid,
          lastMessageId: messageRef.id
        });

        const otherUserId = selectedChat.participants?.find(p => p !== user.uid);
        if (otherUserId) {
          await updateDoc(chatRef, {
            [`unreadCount.${otherUserId}`]: (selectedChat.unreadCount?.[otherUserId] || 0) + 1
          });
        }
      }

      setNewMessage("");
      setSelectedFile(null);
      setIsTyping(false);
      if (fileInputRef.current) fileInputRef.current.value = "";

      // Optimistic update
      setChats(prevChats =>
        prevChats.map(chat =>
          chat.id === selectedChat.id
            ? {
              ...chat,
              lastMessage: newMessage.substring(0, 100) + (newMessage.length > 100 ? "..." : ""),
              lastMessageAt: new Date(),
              lastMessageSender: user.uid
            }
            : chat
        )
      );

    } catch (err) {
      console.error("Error sending message:", err);
      alert("Failed to send message");
    }
  };

  const uploadFile = async (file) => {
    const storageRef = ref(storage, `chat_files/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const markMessagesAsRead = async (messagesList) => {
    if (!selectedChat || !user) return;

    const unreadMessages = messagesList.filter(
      msg => msg.senderId !== user.uid && !msg.readBy?.includes(user.uid)
    );

    if (unreadMessages.length > 0) {
      const batch = [];
      unreadMessages.forEach(msg => {
        let msgRef;
        if (selectedChat.type === "group") {
          msgRef = doc(db, "group_messages", selectedChat.id, "messages", msg.id);
        } else {
          msgRef = doc(db, "direct_messages", selectedChat.id, "messages", msg.id);
        }
        batch.push(updateDoc(msgRef, {
          readBy: arrayUnion(user.uid)
        }));
      });

      await Promise.all(batch);

      const chatRef = doc(db,
        selectedChat.type === "group" ? "groups" : "direct_messages",
        selectedChat.id
      );
      await updateDoc(chatRef, {
        [`unreadCount.${user.uid}`]: 0
      });
    }
  };

  // Load messages for selected chat
  useEffect(() => {
    if (!selectedChat) return;

    let messagesCollection = "";
    let chatId = selectedChat.id;

    if (selectedChat.type === "group") {
      messagesCollection = `group_messages/${chatId}/messages`;
    } else {
      messagesCollection = `direct_messages/${chatId}/messages`;
    }

    const messagesQuery = query(
      collection(db, messagesCollection),
      orderBy("timestamp", "asc"),
      limit(100)
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);

      // Mark messages as read
      markMessagesAsRead(messagesData);

      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    });

    return () => unsubscribe();
  }, [selectedChat, markMessagesAsRead]);

  const startNewDM = async (otherUser) => {
    try {
      const existingDMQuery = query(
        collection(db, "direct_messages"),
        where("participants", "array-contains", user.uid)
      );

      const snapshot = await getDocs(existingDMQuery);
      const existingDM = snapshot.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(otherUser.id);
      });

      if (existingDM) {
        setSelectedChat({
          id: existingDM.id,
          type: "dm",
          ...existingDM.data(),
          otherUser: otherUser
        });
      } else {
        const dmData = {
          participants: [user.uid, otherUser.id],
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
          lastMessage: "",
          lastMessageSender: "",
          unreadCount: {
            [user.uid]: 0,
            [otherUser.id]: 0
          }
        };

        const dmRef = await addDoc(collection(db, "direct_messages"), dmData);
        await setDoc(doc(db, "direct_messages", dmRef.id, "metadata", "info"), {
          chatId: dmRef.id,
          type: "dm",
          participants: [user.uid, otherUser.id]
        });

        const newChat = {
          id: dmRef.id,
          type: "dm",
          ...dmData,
          otherUser: otherUser
        };

        setSelectedChat(newChat);
        setChats(prev => [newChat, ...prev]);
      }

      setShowNewChatModal(false);
      setSearchUsers("");
      setSearchResults([]);
    } catch (err) {
      console.error("Error creating DM:", err);
      alert("Failed to start conversation");
    }
  };

  const createGroup = async () => {
    if (!groupForm.name.trim() || groupForm.participants.length < 1) {
      alert("Group name and at least one participant required");
      return;
    }

    try {
      const groupData = {
        name: groupForm.name.trim(),
        description: groupForm.description || "",
        createdBy: user.uid,
        admin: user.uid,
        members: [user.uid, ...groupForm.participants],
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessage: "",
        lastMessageSender: "",
        unreadCount: {},
        groupImage: groupForm.groupImage || ""
      };

      groupData.members.forEach(memberId => {
        groupData.unreadCount[memberId] = 0;
      });

      const groupRef = await addDoc(collection(db, "groups"), groupData);

      await setDoc(doc(db, "group_messages", groupRef.id, "metadata", "info"), {
        groupId: groupRef.id,
        type: "group",
        createdAt: serverTimestamp()
      });

      const updatePromises = groupData.members.map(async memberId => {
        const userRef = doc(db, "users", memberId);
        await updateDoc(userRef, {
          groups: arrayUnion(groupRef.id),
          updatedAt: serverTimestamp()
        });
      });

      await Promise.all(updatePromises);

      const newGroup = {
        id: groupRef.id,
        type: "group",
        ...groupData
      };

      setSelectedChat(newGroup);
      setChats(prev => [newGroup, ...prev]);

      setShowGroupCreate(false);
      setGroupForm({
        name: "",
        description: "",
        participants: []
      });

      alert("Group created successfully!");
    } catch (err) {
      console.error("Error creating group:", err);
      alert("Failed to create group");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'video/mp4'];
    if (!allowedTypes.includes(file.type)) {
      alert("Allowed file types: Images, PDF, MP4");
      return;
    }

    setSelectedFile(file);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays > 0) {
      return diffDays === 1 ? "Yesterday" : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else if (diffMins > 0) {
      return `${diffMins}m ago`;
    } else {
      return "Just now";
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const leaveGroup = async () => {
    if (!selectedChat || selectedChat.type !== "group" || !user) return;
    if (!window.confirm("Are you sure you want to leave this group?")) return;

    try {
      const groupRef = doc(db, "groups", selectedChat.id);
      await updateDoc(groupRef, {
        members: arrayRemove(user.uid),
        [`unreadCount.${user.uid}`]: deleteField()
      });

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        groups: arrayRemove(selectedChat.id)
      });

      setChats(prev => prev.filter(chat => chat.id !== selectedChat.id));
      setSelectedChat(null);
      alert("You have left the group");
    } catch (err) {
      console.error("Error leaving group:", err);
      alert("Failed to leave group");
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p>Loading chats...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles.emptyState}>
        <h2>Please login to access messages</h2>
        <button className={styles.createBtn} onClick={() => navigate("/login")}>Login</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2 className={styles.sidebarTitle}>Messages</h2>
          <div className={styles.sidebarActions}>
            <button
              className={styles.iconBtn}
              onClick={() => setShowNewChatModal(true)}
              title="New Chat"
            >
              💬
            </button>
            <button
              className={styles.iconBtn}
              onClick={() => setShowGroupCreate(true)}
              title="New Group"
            >
              👥
            </button>
          </div>
        </div>

        <div className={styles.searchContainer}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search chats..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className={styles.chatList}>
          {filteredChats.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>💬</div>
              <p>No messages yet</p>
              <button
                className={styles.createBtn}
                onClick={() => setShowNewChatModal(true)}
              >
                Start a conversation
              </button>
            </div>
          ) : (
            filteredChats.map(chat => (
              <div
                key={chat.id}
                className={`${styles.chatItem} ${selectedChat?.id === chat.id ? styles.chatItemActive : ''}`}
                onClick={() => setSelectedChat(chat)}
              >
                <div className={styles.avatar}>
                  {chat.type === "group" ? (
                    chat.groupImage ? (
                      <img src={chat.groupImage} alt={chat.name} />
                    ) : (
                      <div className={styles.groupAvatar}>
                        {chat.name.charAt(0)}
                      </div>
                    )
                  ) : (
                    chat.otherUser?.photoURL ? (
                      <img src={chat.otherUser.photoURL} alt={chat.otherUser.name} />
                    ) : (
                      <div className={styles.userAvatar}>
                        {chat.otherUser?.name?.charAt(0) || chat.otherUser?.email?.charAt(0) || "U"}
                      </div>
                    )
                  )}
                </div>

                <div className={styles.chatInfo}>
                  <div className={styles.chatName}>
                    {chat.type === "group" ? chat.name : chat.otherUser?.name || chat.otherUser?.email?.split('@')[0]}
                    {chat.type === "group" && (
                      <span className={styles.groupBadge}> (Group)</span>
                    )}
                  </div>
                  <div className={styles.lastMessage}>
                    {chat.lastMessageSender === user.uid ? "You: " : ""}
                    {chat.lastMessage || "No messages yet"}
                  </div>
                </div>

                <div className={styles.chatMeta}>
                  <div className={styles.time}>
                    {formatTime(chat.lastMessageAt)}
                  </div>
                  {chat.unreadCount?.[user.uid] > 0 && (
                    <div className={styles.unreadBadge}>
                      {chat.unreadCount[user.uid]}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.mainArea}>
        {selectedChat ? (
          <>
            <div className={styles.chatHeader}>
              <div className={styles.headerInfo}>
                <div className={styles.headerAvatar}>
                  {selectedChat.type === "group" ? (
                    selectedChat.groupImage ? (
                      <img src={selectedChat.groupImage} alt={selectedChat.name} />
                    ) : (
                      <div>{selectedChat.name.charAt(0)}</div>
                    )
                  ) : (
                    selectedChat.otherUser?.photoURL ? (
                      <img src={selectedChat.otherUser.photoURL} alt={selectedChat.otherUser.name} />
                    ) : (
                      <div>{selectedChat.otherUser?.name?.charAt(0) || "U"}</div>
                    )
                  )}
                </div>
                <div className={styles.headerText}>
                  <h3>
                    {selectedChat.type === "group"
                      ? selectedChat.name
                      : selectedChat.otherUser?.name || selectedChat.otherUser?.email?.split('@')[0]
                    }
                  </h3>
                  <div className={styles.status}>
                    {selectedChat.type === "group" ? (
                      <span>{selectedChat.members?.length || 0} members</span>
                    ) : (
                      <span className={styles.onlineStatus}>● Online</span>
                    )}
                    {isTyping && <span className={styles.typingIndicator}> typing...</span>}
                  </div>
                </div>
              </div>

              <div className={styles.chatActions}>
                {selectedChat.type === "group" && (
                  <button
                    className={styles.iconBtn}
                    onClick={leaveGroup}
                    title="Leave Group"
                  >
                    🚪
                  </button>
                )}
                <button className={styles.iconBtn} title="More">⋮</button>
              </div>
            </div>

            <div className={styles.messagesContainer}>
              {messages.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No messages yet. Say hello!</p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isOwn = msg.senderId === user.uid;
                  return (
                    <div
                      key={msg.id}
                      className={`${styles.messageRow} ${isOwn ? styles.messageRowOwn : ''}`}
                    >
                      <div className={styles.messageBubble}>
                        {msg.type === "image" ? (
                          <div className={styles.messageImage}>
                            <img src={msg.fileUrl} alt="Shared" style={{ maxWidth: '200px', borderRadius: '8px' }} />
                          </div>
                        ) : (
                          <div>{msg.text}</div>
                        )}
                        <div className={styles.messageTime}>
                          {formatMessageTime(msg.timestamp)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className={styles.inputArea}>
              <div className={styles.attachments}>
                <button className={styles.attachBtn} onClick={() => fileInputRef.current.click()}>
                  📎
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                {selectedFile && (
                  <span style={{ fontSize: '0.8rem', marginLeft: '8px' }}>
                    {selectedFile.name.substring(0, 10)}...
                    <button onClick={() => setSelectedFile(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>×</button>
                  </span>
                )}
              </div>

              <textarea
                className={styles.messageInput}
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  handleTyping();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                rows={1}
              />

              <button
                className={styles.sendBtn}
                onClick={sendMessage}
                disabled={!newMessage.trim() && !selectedFile}
              >
                ➤
              </button>
            </div>
          </>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>💬</div>
            <h3>Welcome to Messages</h3>
            <p>Select a chat or start a new conversation</p>
          </div>
        )}
      </div>

      {showNewChatModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>New Message</h3>
              <button
                className={styles.closeBtn}
                onClick={() => setShowNewChatModal(false)}
              >
                ×
              </button>
            </div>

            <input
              type="text"
              className={styles.modalInput}
              placeholder="Search users by email..."
              value={searchUsers}
              onChange={(e) => setSearchUsers(e.target.value)}
              autoFocus
            />

            <div className={styles.chatList} style={{ maxHeight: '300px' }}>
              {searchResults.map(user => (
                <div
                  key={user.id}
                  className={styles.chatItem}
                  onClick={() => startNewDM(user)}
                >
                  <div className={styles.avatar}>
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.name} />
                    ) : (
                      <div>{user.name?.charAt(0) || "U"}</div>
                    )}
                  </div>
                  <div className={styles.chatInfo}>
                    <div className={styles.chatName}>{user.name || user.email}</div>
                    <div className={styles.lastMessage}>{user.email}</div>
                  </div>
                </div>
              ))}
              {searchUsers && searchResults.length === 0 && (
                <p style={{ textAlign: 'center', color: '#888' }}>No users found</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showGroupCreate && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Create New Group</h3>
              <button
                className={styles.closeBtn}
                onClick={() => setShowGroupCreate(false)}
              >
                ×
              </button>
            </div>

            <input
              type="text"
              className={styles.modalInput}
              placeholder="Group Name"
              value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
            />

            <input
              type="text"
              className={styles.modalInput}
              placeholder="Description (Optional)"
              value={groupForm.description}
              onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
            />

            <div style={{ marginBottom: '10px' }}>
              <h4>Add Participants (Email)</h4>
              <input
                type="text"
                className={styles.modalInput}
                placeholder="Search to add..."
                value={searchUsers}
                onChange={(e) => setSearchUsers(e.target.value)}
              />
              <div className={styles.chatList} style={{ maxHeight: '150px', border: '1px solid #1a1a1a' }}>
                {searchResults.map(user => (
                  <div
                    key={user.id}
                    className={styles.chatItem}
                    onClick={() => {
                      // toggle participant
                      if (!groupForm.participants.includes(user.id)) {
                        setGroupForm(prev => ({ ...prev, participants: [...prev.participants, user.id] }));
                      }
                    }}
                  >
                    <div className={styles.chatInfo}>
                      <div className={styles.chatName}>{user.name || user.email}</div>
                    </div>
                    {groupForm.participants.includes(user.id) && <span>✓</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setShowGroupCreate(false)}>Cancel</button>
              <button className={styles.createBtn} onClick={createGroup}>Create Group</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}